/* eslint-disable @typescript-eslint/consistent-type-assertions */

import {
  type ActorRefFrom,
  and,
  enqueueActions,
  type MachineImplementationsFrom,
  not,
  setup,
} from 'xstate';

import {
  createPlaceholderAction,
  createPlaceholderActionWithParams,
  type IngredientPromotion,
} from './helpers';
import { Actions, Actors, Guards, Inputs } from './setup';
import { type Context, type Event, type Input } from './types';

import type { GuardPredicate } from 'xstate/guards';

// ─── Model ───────────────────────────────────────────────────────────────────

const model = setup({
  types: {
    input: {} as Input,
    context: {} as Context,
    events: {} as Event,
  },
  guards: {
    //
    // ─── External Guards ─────────────────────────────────────────────────

    checkIfShouldStartCustomization: () => true,
    checkIfShouldDefaultToMixedDressing: () => false,
    checkIfShouldAutoComplete: () => false,
    checkIfShouldShowOutOfStockIngredients: () => true,

    // ─── Internal Guards ─────────────────────────────────────────────────

    checkIfCustomProduct: Guards.checkIfCustomProduct,
    checkIfExceedsMaxModifications: Guards.checkIfExceedsMaxModifications,
    checkIfExceedsMaxModificationsAfterAddition: Guards.checkIfExceedsMaxModificationsAfterAddition, // prettier-ignore
    checkIfExceedsMaxModificationsAfterRemoval: Guards.checkIfExceedsMaxModificationsAfterRemoval, // prettier-ignore
    checkIfRequiredModification: Guards.checkIfRequiredModification,
    checkIfExceedsAllowedMultipleQuantities: Guards.checkIfExceedsAllowedMultipleQuantities, // prettier-ignore
    checkIfExceedsMaxAggregateQuantity: Guards.checkIfExceedsMaxAggregateQuantity, // prettier-ignore
    checkIfHasOutOfStockModifications: Guards.checkIfHasOutOfStockModifications,
    checkIfInStockIngredient: Guards.checkIfInStockIngredient,
    checkIfDressingsCanBeMixed: Guards.checkIfDressingsCanBeMixed,
    checkIfHasUnmetMinLimitGroup: Guards.checkIfHasUnmetMinLimitGroup,
    checkIfHasMixedDressingDetails: Guards.checkIfHasMixedDressingDetails,
    checkIfShouldEncourageMoreChanges: Guards.checkIfShouldEncourageMoreChanges,
    checkIfDressingDetailsEnabled: Guards.checkIfDressingDetailsEnabled,
    checkIfSplitWouldExceedMaxModifications: Guards.checkIfSplitWouldExceedMaxModifications, // prettier-ignore
    checkIfSplitWouldExceedMaxAggregateQuantity: Guards.checkIfSplitWouldExceedMaxAggregateQuantity, // prettier-ignore
    checkIfAddToBagPending: Guards.checkIfAddToBagPending,
  },
  actions: {
    //
    // ─── External Actions ────────────────────────────────────────────────

    onOutOfStockIngredientsFound: createPlaceholderAction('onOutOfStockIngredientsFound'), // prettier-ignore
    onMaxModificationsExceeded: createPlaceholderAction('onMaxModificationsExceeded'), // prettier-ignore
    onMaxKindQtyExceeded: createPlaceholderAction('onMaxKindQtyExceeded'),
    onMaxKindExceeded: createPlaceholderAction('onMaxKindExceeded'),
    onMinUnmet: createPlaceholderAction('onMinUnmet'),
    onFailedToAddModification: createPlaceholderAction('onFailedToAddModification'), // prettier-ignore
    onModificationAdded: createPlaceholderAction('onModificationAdded'),
    onModificationRemoved: createPlaceholderAction('onModificationRemoved'),
    onIngredientsSwapped:
      createPlaceholderActionWithParams<{ promotions: IngredientPromotion[] }>('onIngredientsSwapped'), // prettier-ignore
    onCustomizationEnd: createPlaceholderAction('onCustomizationEnd'),
    onCustomizationCancel: createPlaceholderAction('onCustomizationCancel'),
    onCustomizationComplete: createPlaceholderAction('onCustomizationComplete'),
    onCustomizationSummary: createPlaceholderAction('onCustomizationSummary'),

    // ─── Internal Actions ────────────────────────────────────────────────

    storeCustomizationData: Actions.storeCustomizationData,
    generateModifications: Actions.generateModifications,
    generateModifiers: Actions.generateModifiers,
    storeLastKnownModifications: Actions.storeLastKnownModifications,
    resetLastKnowModifications: Actions.resetLastKnowModifications,
    resetModifications: Actions.cancelPendingChanges,
    addModification: Actions.addModification,
    removeModification: Actions.removeModification,
    removeModificationCompletely: Actions.removeModificationCompletely,
    calculateCalories: Actions.calculateCalories,
    generateMixedDressingDetails: Actions.generateMixedDressingDetails,
    raiseChangeToOnTheSideIfNeeded: Actions.raiseChangeToOnTheSideIfNeeded,
    raiseGenerateMixedDressingDetailsEvent: Actions.raiseGenerateMixedDressingDetailsEvent, // prettier-ignore
    resetMixedDressingDetails: Actions.resetMixedDressingDetails,
    resetDressingPortions: Actions.resetDressingPortions,
    increaseMixedDressingWeight: Actions.increaseMixedDressingWeight,
    decreaseMixedDressingWeight: Actions.decreaseMixedDressingWeight,
    calculateNetPrice: Actions.calculateNetPrice,
    setCustomName: Actions.setCustomName,
    storeRequiredIngredientName: Actions.storeRequiredIngredientName,
    clearRequiredIngredientName: Actions.clearRequiredIngredientName,
    generateEncouragedChangesList: Actions.generateEncouragedChangesList,
    removeFirstEncouragedChange: Actions.removeFirstEncouragedChange,
    toggleSplitPortion: Actions.toggleSplitPortion,
    storePendingAddToBag: Actions.storePendingAddToBag,
    clearPendingAddToBag: Actions.clearPendingAddToBag,
    reportAddedToBag: Actions.reportAddedToBag,
    reportFailedToAddToBag: Actions.reportFailedToAddToBag,

    // ─── Conditional and Chained Actions ─────────────────────────────────

    updateModificationAfterChange: Actions.updateModificationAfterChange,
    triggerOnOutOfStockIngredientsFound: enqueueActions((args) => {
      const { enqueue, check } = args;

      const shouldShowOutOfStockIngredients = check(
        and(['checkIfShouldShowOutOfStockIngredients', 'checkIfHasOutOfStockModifications']),
      );

      if (!shouldShowOutOfStockIngredients) return;

      enqueue('onOutOfStockIngredientsFound');
    }),
    triggerAutoComplete: enqueueActions((args) => {
      const { enqueue, check } = args;

      const shouldAutoComplete = check(
        and(['checkIfShouldAutoComplete', not('checkIfHasOutOfStockModifications')]),
      );

      if (!shouldAutoComplete) return;

      enqueue.raise({ type: 'customization.complete' });
    }),
    triggerPendingAddToBag: enqueueActions((args) => {
      const { enqueue, check } = args;

      const isAddToBagPending = check('checkIfAddToBagPending');

      if (!isAddToBagPending) return;

      enqueue('clearPendingAddToBag');
      enqueue.raise({ type: 'customization.add-to-bag' });
    }),
    reportPendingAddToBagFailure: enqueueActions((args) => {
      const { enqueue, check } = args;

      const isAddToBagPending = check('checkIfAddToBagPending');

      if (!isAddToBagPending) return;

      enqueue('clearPendingAddToBag');
      enqueue('reportFailedToAddToBag');
    }),
  },
  actors: {
    fetchCustomizationData: Actors.fetchCustomizationData,
    addToBag: Actors.addToBag,
  },
});

// ─────────────────────────────────────────────────────────────────────

export const customizationMachine = model.createMachine({
  initial: 'fetching',
  context: ({ input }) => ({
    productSlug: input.productSlug,
    restaurantSlug: input.restaurantSlug,
    ingredientAssetVersion: input.ingredientAssetVersion,
    lineItemId: input.lineItemId,
    sharedLineItemId: input.sharedLineItemId,
    isDelivery: input.isDelivery,
    defaultDressingWeight: input.defaultDressingWeight,
    isUpsellPricingEnabled: input.isUpsellPricingEnabled,

    product: undefined,
    productLineItem: undefined,
    productRestaurant: undefined,
    quantity: 1,

    modificationLimits: {},
    allModificationsMap: undefined,
    initialIngredientQuantities: new Map(),
    includedModificationsMap: new Map(),
    extrasModificationsMap: new Map(),
    includedModifierGroupsByExtrasName: {},
    extrasModifierGroupsByIncludedName: {},
    modifications: undefined,
    modificationGroups: undefined,
    mixedDressingDetails: [],
    calories: 0,
    netPrice: 0,
    lastKnownModifications: undefined,
    lastKnownMixedDressingDetails: [],
    encouragedChangesList: [],
    customName: undefined,
    requiredIngredientName: undefined,
    shouldAddToBagWhenReady: false,
  }),
  on: {
    'customization.retry-fetch': {
      target: '.fetching',
    },
  },
  states: {
    fetching: {
      invoke: {
        src: 'fetchCustomizationData',
        input: Inputs.getFetchCustomizationDataInput,
        onDone: {
          target: 'undetermined',
          actions: [
            { type: 'storeCustomizationData', params: (args) => args.event.output }, // prettier-ignore
          ],
        },
        onError: {
          target: 'failed-to-fetch',
        },
      },
      on: {
        'customization.add-to-bag': {
          actions: ['storePendingAddToBag'],
        },
      },
    },
    'failed-to-fetch': {
      entry: ['reportPendingAddToBagFailure'],
      on: {
        'customization.add-to-bag': {
          target: 'fetching',
          actions: ['storePendingAddToBag'],
        },
      },
    },
    undetermined: {
      always: [
        {
          target: 'ready.mode.customization',
          guard: 'checkIfShouldStartCustomization',
        },
        {
          target: 'ready.mode.customization',
          guard: 'checkIfCustomProduct',
        },
        {
          target: 'ready.mode.customization',
        },
      ],
    },
    ready: {
      entry: [
        'generateModifications',
        'updateModificationAfterChange',
        'triggerOnOutOfStockIngredientsFound',
        'triggerAutoComplete',

        // NOTE: Keep last. Its raised event then queues behind the dressing events raised above,
        //       so a mixed-in product is added with its dressing details.
        'triggerPendingAddToBag',
      ],
      type: 'parallel',
      states: {
        mode: {
          initial: 'customization',
          states: {
            customization: {
              entry: ['storeLastKnownModifications'],
              exit: ['calculateCalories'],
              initial: 'active',
              states: {
                active: {},
                'encourage-more-changes': {
                  always: [
                    {
                      target: 'active',
                      actions: ['onCustomizationSummary', 'onCustomizationComplete'],
                      guard: and([
                        not('checkIfShouldEncourageMoreChanges'),
                        not('checkIfDressingDetailsEnabled'),
                      ]),
                    },
                    {
                      target: '#customization-details',
                      actions: ['onCustomizationEnd'],
                      guard: not('checkIfShouldEncourageMoreChanges'),
                    },
                    // otherwise, stay in this state and wait for the customer's action
                  ],
                  on: {
                    'customization.continue': {
                      target: 'active',
                    },
                    'customization.complete': {
                      target: 'encourage-more-changes',
                      actions: ['removeFirstEncouragedChange'],
                    },
                  },
                },
                'required-modification': {
                  on: {
                    'customization.continue': 'exiting-required-modification',
                  },
                },
                'exiting-required-modification': {
                  description:
                    'Short-lived state that delays the return to `active` so the required modification modal can smoothly transition to closed state',
                  after: {
                    500: 'active',
                  },
                  exit: ['clearRequiredIngredientName'],
                },
              },
              on: {
                'customization.complete': [
                  {
                    actions: ['onMaxModificationsExceeded'],
                    guard: 'checkIfExceedsMaxModifications',
                  },
                  {
                    actions: ['onMinUnmet'],
                    guard: 'checkIfHasUnmetMinLimitGroup',
                  },
                  {
                    actions: ['onCustomizationSummary', 'onCustomizationComplete'],
                    guard: 'checkIfShouldAutoComplete',
                  },
                  {
                    target: '.encourage-more-changes',
                    actions: ['generateEncouragedChangesList'],
                  },
                ],
                'customization.cancel': {
                  target: '#customization-details',
                  actions: [
                    'resetModifications',
                    'updateModificationAfterChange',
                    'onCustomizationCancel',
                  ],
                },
                'customization.add-modification': [
                  {
                    guard: 'checkIfExceedsMaxModificationsAfterAddition',
                    actions: ['onMaxModificationsExceeded'],
                  },
                  {
                    guard: 'checkIfExceedsAllowedMultipleQuantities',
                    actions: ['onMaxKindQtyExceeded'],
                  },
                  {
                    guard: 'checkIfExceedsMaxAggregateQuantity',
                    actions: ['onMaxKindExceeded'],
                  },
                  {
                    guard: 'checkIfInStockIngredient',
                    actions: [
                      'addModification',
                      'updateModificationAfterChange',
                      'onModificationAdded',
                    ],
                  },
                  {
                    actions: ['onFailedToAddModification'],
                  },
                ],
                'customization.remove-modification': [
                  {
                    guard: 'checkIfRequiredModification',
                    target: '.required-modification',
                    actions: ['storeRequiredIngredientName'],
                  },
                  {
                    guard: 'checkIfExceedsMaxModificationsAfterRemoval',
                    actions: ['onMaxModificationsExceeded'],
                  },
                  {
                    actions: [
                      'removeModification',
                      'updateModificationAfterChange',
                      'onModificationRemoved',
                    ],
                  },
                ],
                'customization.remove-modification-completely': [
                  {
                    guard: 'checkIfRequiredModification',
                    target: '.required-modification',
                    actions: ['storeRequiredIngredientName'],
                  },
                  {
                    actions: [
                      'removeModificationCompletely',
                      'updateModificationAfterChange',
                      'onModificationRemoved',
                    ],
                  },
                ],
                'customization.remove-split-portion-completely': [
                  {
                    guard: 'checkIfRequiredModification',
                    target: '.required-modification',
                    actions: ['storeRequiredIngredientName'],
                  },
                  {
                    guard: {
                      type: 'checkIfSplitWouldExceedMaxModifications',
                      params: { action: 'complete-removal' },
                    },
                    actions: ['onMaxModificationsExceeded'],
                  },
                  {
                    guard: {
                      type: 'checkIfSplitWouldExceedMaxAggregateQuantity',
                      params: { action: 'complete-removal' },
                    },
                    actions: ['onMaxKindExceeded'],
                  },
                  {
                    actions: [
                      {
                        type: 'toggleSplitPortion',
                        params: { action: 'complete-removal' },
                      },
                      'updateModificationAfterChange',
                      'onModificationRemoved',
                    ],
                  },
                ],
                'customization.toggle-split-portion': [
                  {
                    guard: {
                      type: 'checkIfSplitWouldExceedMaxModifications',
                      params: { action: 'toggle' },
                    },
                    actions: ['onMaxModificationsExceeded'],
                  },
                  {
                    guard: {
                      type: 'checkIfSplitWouldExceedMaxAggregateQuantity',
                      params: { action: 'toggle' },
                    },
                    actions: ['onMaxKindExceeded'],
                  },
                  {
                    actions: [
                      {
                        type: 'toggleSplitPortion',
                        params: { action: 'toggle' },
                      },
                      'updateModificationAfterChange',
                    ],
                  },
                ],
              },
            },
            details: {
              id: 'customization-details',
              entry: ['resetLastKnowModifications'],
              on: {
                'customization.start': {
                  target: 'customization',
                },
                'customization.complete': {
                  actions: ['onCustomizationSummary', 'onCustomizationComplete'],
                },
              },
            },
          },
        },
        'dressing-mode': {
          initial: 'undetermined',
          states: {
            undetermined: {
              always: [
                {
                  target: 'mixed-in',
                  guard:
                    and(['checkIfDressingDetailsEnabled', 'checkIfDressingsCanBeMixed', 'checkIfShouldDefaultToMixedDressing']), // prettier-ignore
                },
                {
                  target: 'mixed-in',
                  guard:
                    and(['checkIfDressingDetailsEnabled', 'checkIfDressingsCanBeMixed', 'checkIfHasMixedDressingDetails']), // prettier-ignore
                },
                {
                  target: 'on-the-side',
                },
              ],
            },
            'on-the-side': {
              entry: ['resetMixedDressingDetails', 'updateModificationAfterChange'],
              on: {
                'customization.dressing-mode.mixed-in': {
                  target: 'mixed-in',
                  guard: 'checkIfDressingsCanBeMixed',
                },
                'customization.dressing-mode.toggle': {
                  target: 'mixed-in',
                  guard: 'checkIfDressingsCanBeMixed',
                },
              },
            },
            'mixed-in': {
              entry: ['resetDressingPortions', 'updateModificationAfterChange'],
              on: {
                'customization.dressing-mode.change-to-on-the-side-if-needed': {
                  description:
                    'Change the dressing mode to "on the side" if the active ingredients are not mixable anymore.',
                  target: 'on-the-side',
                  guard: not('checkIfDressingsCanBeMixed'),
                },
                'customization.dressing-mode.on-the-side': {
                  target: 'on-the-side',
                },
                'customization.dressing-mode.toggle': {
                  target: 'on-the-side',
                },
                'customization.dressing-mode.generate-mixed-dressings-details': {
                  actions: ['generateMixedDressingDetails', 'calculateCalories'],
                },
              },
            },
          },
          on: {
            'customization.dressing.change-portions': {
              actions: Actions.changeDressingPortions,
            },
            'customization.dressing.change-weight': {
              actions: Actions.changeDressingWeight,
            },
            'customization.dressing.increase-portions': [
              {
                guard: 'checkIfExceedsMaxModificationsAfterAddition',
                actions: ['onMaxModificationsExceeded'],
              },
              {
                guard: 'checkIfExceedsMaxAggregateQuantity',
                actions: ['onMaxKindExceeded'],
              },
              {
                guard: 'checkIfInStockIngredient',
                actions: [
                  'addModification',
                  'updateModificationAfterChange',
                  'onModificationAdded',
                ],
              },
              {
                actions: ['onFailedToAddModification'],
              },
            ],
            'customization.dressing.decrease-portions': [
              {
                guard: 'checkIfExceedsMaxModificationsAfterRemoval',
                actions: ['onMaxModificationsExceeded'],
              },
              {
                actions: [
                  'removeModification',
                  'updateModificationAfterChange',
                  'onModificationRemoved',
                ],
              },
            ],
            'customization.dressing.increase-weight': [
              { actions: ['increaseMixedDressingWeight', 'calculateCalories'] },
            ],
            'customization.dressing.decrease-weight': [
              { actions: ['decreaseMixedDressingWeight', 'calculateCalories'] },
            ],
          },
        },
        cart: {
          initial: 'idle',
          states: {
            idle: {
              on: {
                'customization.add-to-bag': {
                  target: 'checking',
                },
              },
            },
            checking: {
              always: [
                {
                  target: 'failed',
                  guard: 'checkIfExceedsMaxModifications',
                },
                {
                  target: 'failed',
                  guard: 'checkIfHasUnmetMinLimitGroup',
                },
                {
                  target: 'adding',
                },
              ],
            },
            adding: {
              invoke: {
                src: 'addToBag',
                input: Inputs.getAddToBagInput,
                onDone: {
                  target: 'added',
                },
                onError: {
                  target: 'failed',
                },
              },
            },
            added: {
              entry: ['reportAddedToBag'],
            },
            failed: {
              entry: ['reportFailedToAddToBag'],
              on: {
                'customization.add-to-bag': {
                  target: 'checking',
                },
              },
            },
          },
        },
      },
      on: {
        'customization.set-custom-name': {
          actions: 'setCustomName',
        },
      },
    },
  },
});

// ─── Types ───────────────────────────────────────────────────────────────────

export type CustomizationMachineActorRef = ActorRefFrom<typeof customizationMachine>; // prettier-ignore
export type CustomizationMachineImplementations = MachineImplementationsFrom<typeof customizationMachine>; // prettier-ignore
